<template>
  <v-data-iterator
    :items="listState.items"
    :loading="listState.loading"
    :options.sync="listState.options"
    :value="listState.selected"
    :server-items-length="listState.total"
    :items-per-page="itemsPerPage"
    :hide-default-footer="true"
    @update:items-per-page="listState.reload"
    @update:page="listState.reload"
  >
    <template v-slot:header v-if="!headerHidden">
      <v-toolbar flat color="blue lighten-5" v-if="listState.selected.length">
        <!-- Search filter when bulk action active -->
        <form-filter
          :filters="getEnabledFilters"
          @remove="disableFilter"
          v-model="currentFilter"
        >
          <template
            v-for="filter in getEnabledFilters"
            v-slot:[filter.source]="props"
          >
            <!-- @slot Custom component filters. -->
            <slot :name="`filter.${filter.source}`" v-bind="props"></slot>
          </template>
        </form-filter>
        <v-spacer></v-spacer>
        <div>
          <!-- @slot Custom bulk actions, ideal place for VaBulkActionButton. -->
          <slot name="bulk.actions"></slot>
          <va-bulk-delete-button
            v-if="!disableBulkDelete"
          ></va-bulk-delete-button>
        </div>
      </v-toolbar>
      <v-toolbar flat v-else>
        <form
          class="d-flex align-center"
          v-if="association"
          @submit.prevent="onAssociate"
        >
          <va-autocomplete-input
            :label="$admin.getResource(resource).singularName"
            :resource="resource"
            :reference="resource"
            v-model="associationId"
            hide-details
            :filled="false"
          ></va-autocomplete-input>
          <va-associate-button type="submit"></va-associate-button>
        </form>
        <form-filter
          :filters="getEnabledFilters"
          @remove="disableFilter"
          v-model="currentFilter"
        >
          <template
            v-for="filter in getEnabledFilters"
            v-slot:[filter.source]="props"
          >
            <!-- @slot Custom component for alwaysOn filters. -->
            <slot :name="`filter.${filter.source}`" v-bind="props"></slot>
          </template>
        </form-filter>

        <v-spacer>
          <!-- Filter chips (active filter values)-->
          <slot name="activeFilterChips">
            <form-filter-values-list
              :domains-list="domainsList"
              :labels-list="labelsList"
              :goals-list="goalsList"
              :owners-list="ownersList"
              :hide-domain-filter="hideDomainFilter"
              :filter-values="currentFilter"
              @filter-removed="updateFiltersValues"
            >
            </form-filter-values-list>
          </slot>
          <!-- Filter chips (active filter values) END -->
        </v-spacer>

        <va-create-button
          v-if="!disableCreate"
          :disable-redirect="disableCreateRedirect"
          :label="createButtonLabel"
          :resource="resource"
          @click="onAction('create')"
        ></va-create-button>
        <!-- @slot Put here some global action with components based on VaActionButton. -->
        <slot name="actions">
          <div v-if="dynamicColumnsEnabled">
            <dynamic-columns
              v-model="columns"
              @update:value="updateActiveColumns"
            />
          </div>
          <div
            :class="
              'custom-filter' + dynamicColumnsEnabled && 'spacing-left--16'
            "
            v-if="getFiltersList && getFiltersList.length"
          >
            <!-- filter dropdown-->
            <form-filter-select
              :domains-list="domainsList"
              :filter-values="currentFilter"
              :filters-list="getFiltersList"
              :labels-list="labelsList"
              :goals-list="goalsList"
              :owners-list="ownersList"
              @apply-filters="updateFiltersValues"
            >
            </form-filter-select>
          </div>
        </slot>
        <va-export-button
          v-if="!disableExport"
          :resource="resource"
          text
          :options="listState.options"
          :filter="getCurrentFilter"
        ></va-export-button>
      </v-toolbar>
    </template>
    <template v-slot:default>
      <!--
        @slot Main list layout placeholder. Mainly for VaDataTable but can be anything else.
        @binding {string} resource Name of resource.
        @binding {array} items Result of data fetched from API.
        @binding {boolean} loading Loading indicator.
        @binding {number} total Total count from server-side.
      -->
      <slot v-bind="listState"></slot>
    </template>
    <template v-slot:loading>
      <slot :resource="resource" :loading="listState.loading"></slot>
    </template>
    <template v-slot:no-data>
      <slot
        v-if="displayNoResultsUi"
        name="no-results"
        :resource="resource"
        :list-state="listState"
      ></slot>
      <slot
        v-else
        name="no-data"
        :resource="resource"
        :list-state="listState"
      ></slot>
    </template>
    <template v-slot:no-results>
      <slot :resource="resource"></slot>
    </template>
    <template v-slot:footer>
      <!-- @slot Best place for any custom pagination. -->

      <div v-if="!footerHidden" class="v-data-footer">
        <div class="v-data-footer__select">
          Items per page:
          <v-select
            label=""
            :items="itemsPerPageOptions"
            :value="listState.options.itemsPerPage"
            :disabled="disableItemsPerPage"
            :hide-details="true"
            min-width="75px"
            :auto="true"
            @input="onChangeItemsPerPage"
          >
          </v-select>
        </div>
        <div class="v-data-footer__pagination">
          {{ getPaginationInfo }}
        </div>
        <div class="v-data-footer__icons-before">
          <v-btn
            :disabled="disablePrevPageIcon || disablePagination"
            :icon="true"
            @click="onPreviousPage"
          >
            <v-icon> mdi-chevron-left </v-icon>
          </v-btn>
        </div>
        <span
          v-for="page in pages"
          :class="getPageClass(page)"
          @click="onPageOpen(page)"
        >
          {{ page }}
        </span>
        <div class="v-data-footer__icons-after">
          <v-btn
            :disabled="disableNextPageIcon || disablePagination"
            :icon="true"
            @click="onNextPage"
          >
            <v-icon> mdi-chevron-right </v-icon>
          </v-btn>
        </div>
      </div>
    </template>
  </v-data-iterator>
</template>

<script>
import Resource from "../../../mixins/resource";
import Search from "../../../mixins/search";
import FormFilter from "../../internal/FormFilter";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import FormFilterSelect from "../../../components/internal/FormFilterSelect.vue";
import FormFilterValuesList from "../../../components/internal/FormFilterValuesList.vue";
import DynamicColumns from "../../../components/internal/DynamicColumns.vue";
/**
 * List data iterator component, perfect for list CRUD page as well as any resource browsing standalone component.
 * Allow resource paginating and filtering. Use current query string context for initial state by default.
 * The list layout on default slot is fully customizable and can be used for separate `VaDataTable` component.
 */
export default {
  mixins: [Resource, Search],
  components: {
    FormFilter,
    FormFilterSelect,
    FormFilterValuesList,
    DynamicColumns,
  },
  provide() {
    return {
      listState: this.listState,
    };
  },
  props: {
    /*
     * we need list of domain for the filters
     * and because on loading list component always made call to domains
     * it's better to set domainsList like prop instead of making a new call
     */
    domainsList: {
      type: Array,
      default: () => [],
    },
    labelsList: {
      type: Array,
      default: () => [],
    },
    goalsList: {
      type: Array,
      default: () => [],
    },
    ownersList: {
      type: Array,
      default: () => [],
    },
    /**
     * Exposed filters, editable through advanced filter form, update URL query string if not disabled.
     * Sent to your data provider inside `filter` params.
     * Use specific `alwaysOn` property for each filter you want to be visible and not removable.
     * Valid properties are `source`, `type`, `label`, `alwaysOn`, `attributes`.
     */
    filters: {
      type: Array,
      default: () => [],
    },
    /**
     * List of available selections of items per page.
     */
    itemsPerPageOptions: {
      type: Array,
      default() {
        return (
          get(this.$admin.options, "list.itemsPerPageOptions") || [
            5, 10, 15, 25, 50, 100,
          ]
        );
      },
    },
    /**
     * Disable real time update of URL query string on any browsing action as pagination, sorting, filtering, etc.
     */
    disableQueryString: Boolean,
    /**
     * Overwrite default create button label text.
     */
    createButtonLabel: String,
    /**
     * Hide default footer, notably default pagination.
     */
    hideDefaultFooter: Boolean,
    /**
     * Disable pagination, will hide default footer.
     */
    disablePagination: Boolean,
    /**
     * Hide all header toolbar, so neither filters nor any create or export actions.
     */
    hideHeader: Boolean,
    /**
     * Force disabling of create button, shown by default if create resource action available.
     */
    disableCreate: Boolean,
    /**
     * Disable the export button.
     * The export allows client-side CSV download and keep active filters while disabling pagination.
     */
    disableExport: {
      type: Boolean,
      default() {
        return get(this.$admin.options, "list.disableExport") || false;
      },
    },
    /**
     * Disable bulk delete
     */
    disableBulkDelete: {
      type: Boolean,
      default() {
        return get(this.$admin.options, "list.disableBulkDelete") || false;
      },
    },
    /**
     * Disable create redirection. Will force create button to show.
     */
    disableCreateRedirect: Boolean,
    /**
     * Disable the global search.
     */
    disableGlobalSearch: {
      type: Boolean,
      default() {
        return get(this.$admin.options, "list.disableGlobalSearch") || false;
      },
    },
    /**
     * Key parameter of the global search query.
     */
    globalSearchQuery: {
      type: String,
      default: "q",
    },
    /**
     * Association infos object in case of this list is related to a current show or edit resource page.
     * Enable the association between resources directly by an autocomplete an associate action.
     */
    association: {
      type: Object,
      default: () => {},
    },
    /**
     * No-results UI is displayed whenever we have an entity list inside of single domain view
     * (because the list is automatically filtered by domain)
     * That is why when a list is inside of a single-domain view, we have to ignore the 'domain' filter when deciding
     * whether we should show no-results or no-data UI.
     */
    ignoreDomainFilter: {
      type: Boolean,
      default: false,
    },
    /**
     * Hides domain filter
     */
    hideDomainFilter: Boolean,
    /**
     * This function can mutate listState object (!!!)
     */
    updateListStateDataFn: {
      type: Function,
      default: (_items, _filters) => {
        return {};
      },
    },
    /**
     * Appends filter query without altering the filters UI.
     */
    appendFilterQueryFn: {
      type: Function,
      default: (filters) => {
        return filters;
      },
    },
    dynamicColumnsEnabled: {
      type: Boolean,
      default: false,
    },
    // Visible columns filter
    columns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loaded: false,
      currentFilter: {},
      enabledFilters: [],
      associationId: null,
      listState: {
        resource: this.resource,
        items: [],
        loading: false,
        total: 0,
        selected: [],
        options: {},
        reload: () => {
          this.fetchData();
          this.updateQuery();
        },
        excludedItemsCount: 0,
      },
    };
  },
  async mounted() {
    await this.initFiltersFromQuery();
    this.loaded = true;
    this.fetchData();
  },
  computed: {
    getPaginationInfo() {
      const itemsLength = this.listState.total;
      let pageStart = this.pageStart;
      let pageStop = this.pageStop;

      if (this.listState.total && this.listState.options.itemsPerPage) {
        pageStart = this.pageStart + 1;
        pageStop =
          itemsLength < this.pageStop || this.pageStop < 0
            ? itemsLength
            : this.pageStop;
      }

      return `${pageStart}-${pageStop} of ${itemsLength}`;
    },

    pageCount() {
      return this.listState.options.itemsPerPage <= 0
        ? 1
        : Math.ceil(this.listState.total / this.listState.options.itemsPerPage);
    },

    pageStart() {
      if (
        this.listState.options.itemsPerPage === -1 ||
        !this.listState.items.length
      )
        return 0;

      return (
        (this.listState.options.page - 1) * this.listState.options.itemsPerPage
      );
    },

    pageStop() {
      if (this.listState.options.itemsPerPage === -1)
        return this.listState.total;
      if (!this.listState.items.length) return 0;

      return Math.min(
        this.listState.total,
        this.listState.options.page * this.listState.options.itemsPerPage
      );
    },

    disablePrevPageIcon() {
      return this.listState.options.page === 1;
    },

    disableNextPageIcon() {
      return (
        this.listState.options.itemsPerPage <= 0 ||
        this.listState.options.page * this.listState.options.itemsPerPage >=
          this.listState.total ||
        this.pageStop < 0
      );
    },

    pages() {
      const pages = [];

      if (this.pageCount <= 6) {
        return Array.from({ length: this.pageCount }, (_, index) => index + 1);
      } else {
        const currentPage = this.listState.options.page;

        pages.push(1);

        if (currentPage > 3) {
          pages.push("...");
        }

        if (currentPage === 1 || currentPage === 2 || currentPage === 3) {
          pages.push(2, 3, 4, 5);
        } else if (
          currentPage === this.pageCount ||
          currentPage === this.pageCount - 1 ||
          currentPage === this.pageCount - 2
        ) {
          pages.push(
            this.pageCount - 4,
            this.pageCount - 3,
            this.pageCount - 2,
            this.pageCount - 1,
            this.pageCount
          );
        } else {
          pages.push(currentPage - 1, currentPage, currentPage + 1);
        }

        if (currentPage < this.pageCount - 2) {
          pages.push("...");
        }

        if (
          currentPage !== this.pageCount &&
          currentPage !== this.pageCount - 1 &&
          currentPage !== this.pageCount - 2
        ) {
          pages.push(this.pageCount);
        }

        return pages;
      }
    },

    getFiltersList() {
      return this.filters.filter((filterItem) => {
        return !filterItem.alwaysOn;
      });
    },
    /**
     * If true, displays no-results slot instead of no-data slot
     */
    displayNoResultsUi() {
      if (
        this.ignoreDomainFilter &&
        Object.keys(this.getCurrentFilter).length === 1
      )
        return false;
      // If any of the filters are set, then displays no-results instead of no-data
      return !!Object.keys(this.getCurrentFilter).length;
    },
    footerHidden() {
      if (this.hideDefaultFooter) return true;
      if (this.disablePagination) return true;
      // If no filters are set and no items are in the list, hide the footer.
      if (!this.displayNoResultsUi && !this.listState?.items?.length)
        return true;

      return false;
    },
    headerHidden() {
      // Makes it so that header remains visible while list is loading
      if (this.listState.loading) return false;
      if (this.listState?.excludedItemsCount > 0) return false;
      if (this.hideHeader) return true;
      // If no filters are set and no items are in the list, hide the header.
      if (!this.displayNoResultsUi && !this.listState?.items?.length)
        return true;

      return false;
    },
    getCurrentFilter() {
      /**
       * Get clean filter, do not take empty value but booleans
       */
      return Object.keys(this.currentFilter).reduce((o, key) => {
        let value = this.currentFilter[key];

        return {
          ...o,
          ...((!isEmpty(value) ||
            typeof value === "number" ||
            typeof value === "boolean") && {
            [key]: value,
          }),
        };
      }, {});
    },
    getFilters() {
      let filters = [];

      if (!this.disableGlobalSearch) {
        /**
         * Add global search filter
         */
        filters = [
          {
            source: this.globalSearchQuery,
            label: this.$t("va.datatable.search"),
            alwaysOn: true,
            attributes: { appendIcon: "mdi-magnify" },
          },
        ];
      }

      return [...filters, ...this.filters]
        .map((f) => {
          return typeof f === "string"
            ? {
                source: f,
              }
            : f;
        })
        .map((f) => {
          return {
            ...f,
            type: f.type || "text",
            label:
              f.label ||
              this.$admin.getSourceLabel(this.resource, f.labelKey || f.source),
          };
        });
    },
    getEnabledFilters() {
      return this.getFilters.filter((f) => {
        return f.alwaysOn || this.enabledFilters.includes(f.source);
      });
    },
    getDisabledFilters() {
      return this.getFilters.filter((f) => {
        return !f.alwaysOn && !this.enabledFilters.includes(f.source);
      });
    },
  },
  watch: {
    filter: {
      handler(newVal) {
        for (const newValElement of Object.keys(newVal)) {
          this.enabledFilters.push(newValElement);
        }
        this.currentFilter = newVal || {};
      },
      immediate: true,
      deep: true,
    },
    "$store.state.api.refresh"(newVal) {
      if (newVal) {
        this.fetchData();
      }
    },
    "listState.selected"(val) {
      /**
       * Triggered on bulk actions change.
       */
      this.$emit("update:selected", val);
    },
    "listState.options"(val) {
      /**
       * Triggered on pagination change.
       */
      this.$emit("update:options", val);
    },
    "listState.total"(val) {
      /**
       * Triggered on total items change
       */
      this.$emit("update:total", val);
    },
    "listState.loading"(val) {
      /**
       * Triggered on items load
       */
      this.$emit("update:loading", val);
    },
    currentFilter(newVal) {
      // After any filtering, return to the first page
      this.listState.options && (this.listState.options.page = 1);
      this.fetchData();
      this.updateQuery();

      /**
       * Triggered on filter change.
       */
      this.$emit("update:filter", newVal);
    },
    displayNoResultsUi(val) {
      this.$emit("no-data", val);
    },
  },
  methods: {
    updateActiveColumns(selectedColumns) {
      this.$emit("update:columns", selectedColumns);
    },
    onPreviousPage() {
      this.listState.options.page -= 1;
      this.listState.expanded = [];
    },

    onNextPage() {
      this.listState.options.page += 1;
      this.listState.expanded = [];
    },

    onChangeItemsPerPage(itemsPerPage) {
      this.listState.options.itemsPerPage = itemsPerPage;
      // Return to first page after page size change
      this.listState.options.page = 1;
    },

    onPageOpen(page) {
      if (!isNaN(page)) {
        this.listState.options.page = page;
        this.listState.expanded = [];
      }
    },

    getPageClass(page) {
      if (!isNaN(page)) {
        if (this.listState.options.page === page) {
          return "active number";
        }
        return "number";
      }
      return "";
    },

    /**
     * We want filters to only be applied once "Apply (x) button is clicked", instead
     * of every time a new filter is added inside of filters dropdown.
     */
    updateFiltersValues(filterQuery) {
      for (const [key, value] of Object.entries(filterQuery)) {
        this.currentFilter = {
          ...this.currentFilter,
          [key]: value,
        };
      }
    },
    async initFiltersFromQuery() {
      let options = {
        page: 1,
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
      };

      if (this.disableQueryString) {
        this.listState.options = options;
        return;
      }

      /**
       * Apply current route query into options
       */
      const { perPage, page, sortBy, sortDesc, filter } = this.$route.query;

      if (page) {
        options.page = parseInt(page, 10);
      }

      if (perPage) {
        options.itemsPerPage = parseInt(perPage, 10);
      }

      if (sortBy) {
        options.sortBy = sortBy.split(",");
      }

      if (sortDesc) {
        options.sortDesc = sortDesc.split(",").map((bool) => bool === "true");
      }

      this.listState.options = options;

      /**
       * Enable active filters from query
       */
      if (filter) {
        const filterValues = JSON.parse(filter);

        for (let prop in filterValues) {
          let filter = this.getFilters.find((f) => f.source === prop);

          if (filter) {
            this.enableFilter(filter);
          }
        }
        this.updateFiltersValues(filterValues);
      }
    },
    enableFilter(filter) {
      this.enabledFilters.push(filter.source);
    },
    disableFilter(filter) {
      this.enabledFilters.splice(this.enabledFilters.indexOf(filter.source), 1);
    },
    updateQuery() {
      if (this.disableQueryString || isEmpty(this.listState.options)) {
        return;
      }

      /**
       * Update query router
       */
      let { itemsPerPage, page, sortBy, sortDesc } = this.listState.options;
      let query = {
        page,
        ...(!this.disableItemsPerPage && { perPage: itemsPerPage }),
      };

      if (!isEmpty(sortBy)) {
        query.sortBy = sortBy.join(",");
      }

      if (!isEmpty(sortDesc)) {
        query.sortDesc = sortDesc.join(",");
      }

      if (!isEmpty(this.getCurrentFilter)) {
        query.filter = JSON.stringify(this.getCurrentFilter);
      }

      this.$router.push({ query }).catch(() => {});
    },
    async fetchData() {
      if (!this.loaded || isEmpty(this.listState.options)) {
        return;
      }

      this.listState.loading = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.listState.options;

      let params = {
        fields: this.getFieldsQuery(this.resource, this.fields),
        include: isEmpty(this.include)
          ? this.currentResource.include
          : this.include,
        sort: (sortBy || []).map((by, index) => {
          return { by, desc: sortDesc[index] };
        }),
        filter: this.appendFilterQueryFn(this.getCurrentFilter),
      };

      if (!this.disablePagination) {
        params.pagination = {
          page,
          ...(!this.disableItemsPerPage && { perPage: itemsPerPage }),
        };
      }

      /**
       * Load paginated and sorted data list
       */
      let { data, total } = await this.$store.dispatch(
        `${this.resource}/getList`,
        params
      );

      const listStateData = await this.updateListStateDataFn(
        data,
        this.getCurrentFilter
      );

      /**
       * Update state without cloning
       */
      let newState = {
        items: data,
        loading: false,
        total,
        selected: [],
        options: this.listState.options,
        ...listStateData,
      };

      if (newState.options.page > 1 && newState.items.length === 0) {
        newState.options.page--;
      }

      for (let key in newState) {
        this.listState[key] = newState[key];
      }
    },
    getFieldsQuery(resource, sources, fields = {}) {
      sources.forEach((s) => {
        /**
         * Dot notation support
         */
        let lastIndex = s.lastIndexOf(".");

        if (lastIndex === -1) {
          /**
           * This is simple field
           * Add simple field to main resource
           */
          let f = fields[resource] || [];
          fields[resource] = [...f, s];
          return;
        }

        /**
         * This is field of relation
         * Recursively for nested relation
         */
        let relation = s.substr(0, lastIndex);
        let f = fields[relation] || [];
        fields[relation] = [...f, s.substr(lastIndex + 1)];
      });
      return fields;
    },
    async onDelete() {
      this.fetchData();
    },
    onAction(action) {
      if (!this.disableCreateRedirect) {
        return;
      }

      /**
       * Allows you to use global action event for custom action on your side.
       */
      this.$emit("action", {
        action,
        title: this.currentResource.getTitle("create"),
      });
    },
    async onAssociate() {
      await this.$store.dispatch(`${this.resource}/update`, {
        id: this.associationId,
        data: {
          [`add_${this.association.source}`]: this.association.id,
        },
      });

      this.fetchData();

      /**
       * Triggered on successful association of resource item.
       */
      this.$emit("associated", this.associationId);
      this.associationId = null;
    },
  },
};
</script>

<style scoped lang="scss"></style>
